import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Logo } from "./Logo";
import { Footer } from "./Footer";
import { WhatsAppFloat } from "./WhatsAppFloat";
import { useLanguage } from "../contexts/LanguageContext";

/**
 * Layout for legal subpages (Impressum, Datenschutz).
 * - Sticky compact header with logo + back-link + language switch
 * - Main slot for content
 * - Shared site Footer + Floating WhatsApp button
 */
export const LegalLayout = ({ title, lastUpdated, children }) => {
  const { t, language, setLanguage } = useLanguage();

  // Always start at top when navigating to a legal page
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0D14] text-white flex flex-col">
      <header
        className="sticky top-0 z-40 bg-[#0A0D14]/85 backdrop-blur-xl border-b border-white/10"
        data-testid="legal-header"
      >
        <div className="max-w-5xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
          <Link
            to="/"
            className="flex items-center"
            data-testid="legal-logo-link"
            aria-label={t.nav.back_home}
          >
            <Logo size="sm" />
          </Link>

          <div className="flex items-center gap-3">
            <div
              className="flex items-center gap-1 rounded-full bg-white/5 border border-white/10 p-1"
              data-testid="legal-language-switcher"
            >
              <button
                onClick={() => setLanguage("de")}
                className={`text-[11px] font-heading uppercase tracking-wider px-2.5 py-1 rounded-full transition-all ${
                  language === "de"
                    ? "bg-[#E63946] text-white"
                    : "text-white/70 hover:text-white"
                }`}
                data-testid="legal-lang-de-btn"
              >
                DE
              </button>
              <button
                onClick={() => setLanguage("ku")}
                className={`text-[11px] font-heading uppercase tracking-wider px-2.5 py-1 rounded-full transition-all ${
                  language === "ku"
                    ? "bg-[#E63946] text-white"
                    : "text-white/70 hover:text-white"
                }`}
                data-testid="legal-lang-ku-btn"
              >
                KU
              </button>
            </div>

            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs sm:text-sm font-heading uppercase tracking-wider text-white/70 hover:text-white transition-colors"
              data-testid="legal-back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">{t.legal.back}</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 px-5 md:px-8 py-16 md:py-24">
        <article className="max-w-3xl mx-auto">
          <header className="mb-10 md:mb-14">
            <p className="font-script text-[#E63946] text-base md:text-lg mb-2">
              — {title}
            </p>
            <h1
              className="font-heading font-bold uppercase tracking-tight text-white leading-[0.95] text-4xl sm:text-5xl md:text-6xl"
              data-testid="legal-page-title"
            >
              {title}
            </h1>
            {lastUpdated && (
              <p className="mt-4 text-xs text-white/40 font-mono">
                {t.legal.last_updated}: {lastUpdated}
              </p>
            )}
          </header>

          <div className="legal-content space-y-10 text-white/75 leading-relaxed">
            {children}
          </div>
        </article>
      </main>

      <Footer t={t} />
      <WhatsAppFloat label={t.whatsapp_float} />
    </div>
  );
};

/* Helper section components for legal pages */
export const LegalSection = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="font-heading uppercase tracking-tight text-xl md:text-2xl text-white">
      {title}
    </h2>
    <div className="space-y-3 text-[15px] md:text-base leading-relaxed text-white/70">
      {children}
    </div>
  </section>
);

export const LegalRow = ({ label, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
    <span className="text-xs uppercase tracking-wider text-white/40 font-heading sm:w-44 sm:flex-shrink-0">
      {label}
    </span>
    <span className="text-white/80 break-words">{children}</span>
  </div>
);
